import { Component } from 'react';

/**
 * Error boundary for lazy-loaded routes and components
 * Catches chunk loading failures and other runtime errors
 */
class LazyLoadErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Lazy load error:', error, errorInfo);
  }

  handleRetry = () => {
    // Force a full page reload to clear any stale chunks
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isChunkError =
        this.state.error?.message?.includes('Failed to fetch') ||
        this.state.error?.message?.includes('chunk') ||
        this.state.error?.message?.includes('Loading');

      return (
        <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4">
          <div className="max-w-md w-full">
            <div className="apple-card bg-surface-secondary p-8 text-center">
              <div className="w-16 h-16 bg-status-danger/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-status-danger"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              <h2 className="text-xl font-semibold text-text-primary mb-2">
                {isChunkError ? 'Failed to Load Page' : 'Something Went Wrong'}
              </h2>

              <p className="text-text-secondary mb-6">
                {isChunkError
                  ? 'Unable to load the requested page. This may be due to a network issue or outdated cached files.'
                  : 'An unexpected error occurred. Please try refreshing the page.'}
              </p>

              <button
                onClick={this.handleRetry}
                className="apple-button-primary w-full"
                aria-label="Reload page to retry loading"
              >
                Reload Page
              </button>

              {import.meta.env.DEV && this.state.error && (
                <details className="mt-4 text-left">
                  <summary className="text-xs text-text-secondary cursor-pointer hover:text-text-primary">
                    Error Details (dev only)
                  </summary>
                  <pre className="mt-2 p-3 bg-surface-tertiary rounded text-xs text-status-danger overflow-auto">
                    {this.state.error.message}
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default LazyLoadErrorBoundary;
